import * as React from 'react';
import { TokenStore } from './stores/TokenStore';
import { FlightLogger } from './metrics/FlightLogger';
import { AdminClientProvisioner } from './clients/AdminClientProvisioner';
import { AdminTokenProvisioner } from './clients/AdminTokenProvisioner';
import { OrgStore } from './stores/OrgStore';
import { AccountsPortalClient } from '@journeyapps-platform/sdk-accounts';
import {
  AppsBank,
  DeploymentBank,
  DeveloperBank,
  DeveloperInviteBank,
  DeveloperInviteStore,
  IntegrationBank,
  PolicyBank,
  RoleBank,
  PlansBank,
  IntercomTicketBank
} from '@journeyapps-platform/lib-journey-core';
import { Environments, getRegionsForEnvironment, Region, TRIAL_REGIONS } from './utils/environments';
import { LayerManager } from './layers/LayerManager';
import { CardinalClient } from '@journeyapps-platform/sdk-cardinal';
import { Permission } from '@journeyapps-platform/types-cardinal';
import { DialogStore } from './stores/DialogStore';
import { WebNetworkClient } from '@journeyapps-labs/common-sdk';
import { AccountsV5Client } from '@journeyapps-platform/sdk-accounts-hub';
import { NotificationStore } from './stores/NotificationStore';
import { ProfileStore } from './stores/ProfileStore';
import { MFAStore } from './stores/MFAStore';
import { makeObservable, observable } from 'mobx';
import { JourneyTheme, Theme } from './theme';
import { ENV } from './env';
import { AdminNetworkClient } from './clients/AdminNetworkClient';

export type CreateSystemParams = typeof ENV & {
  recaptcha: {
    v2_site_key: string;
    v3_site_key: string;
  };
  DEV: boolean;
  environment: Environments;
  isMobile: boolean;
  isJourneyUser: boolean;
};

export class System {
  @observable
  context: CreateSystemParams;

  flightLogger: FlightLogger;
  clientProvisioner: AdminClientProvisioner;

  // Stores
  tokenStore: TokenStore;
  orgStore: OrgStore;
  appsStore: AppsBank;
  developStore: DeveloperBank;
  developerInviteStore: DeveloperInviteBank;
  roleStore: RoleBank;
  integrationStore: IntegrationBank;
  deployConfigStore: DeploymentBank;
  plansBank: PlansBank;
  dialogStore: DialogStore;
  notificationStore: NotificationStore;
  profileStore: ProfileStore;
  mfaStore: MFAStore;
  policyStore: PolicyBank;
  intercomBank: IntercomTicketBank;

  // Clients
  sessionAccountsPortalClient: AccountsPortalClient;
  accountV5Client: AccountsV5Client<WebNetworkClient>;

  // @ts-ignore
  cardinalClient: CardinalClient<WebNetworkClient>;
  layerManager: LayerManager;
  userPermissionsPromise?: Promise<Permission[]>;

  theme: Theme;

  constructor(params: CreateSystemParams) {
    this.context = params;
    this.theme = JourneyTheme;
    const tokenProvisioner = new AdminTokenProvisioner({
      accountsPortalClient: new AccountsPortalClient({
        endpoint: params.ACCOUNTS_PORTAL_URL,
        client: new AdminNetworkClient()
      })
    });

    this.clientProvisioner = new AdminClientProvisioner({
      alpha_url: this.context.ALPHA_URL,
      accounts_hub_url: this.context.ACCOUNTS_URL,
      accounts_portal_url: this.context.ACCOUNTS_PORTAL_URL,
      cardinal_url: this.context.CARDINAL_URL,
      source_service_url: this.context.SOURCE_SERVICE_URL,
      deploy_service_url: this.context.DEPLOY_SERVICE_URL,
      flightlog_url: this.context.FLIGHT_LOG_URL,
      cerberus_url: this.context.CERBERUS_URL,
      charging_station_url: this.context.CHARGING_STATION_URL,
      container_depot_url: this.context.CONTAINER_DEPOT_URL,
      intercom_url: this.context.INTERCOM_URL,
      token_provisioner: tokenProvisioner
    });

    const opts = {
      banks: () => {
        return {
          orgBank: this.orgStore,
          appsBank: this.appsStore,
          developerBank: this.developStore,
          roleBank: this.roleStore,
          deploymentBank: this.deployConfigStore,
          integrationBank: this.integrationStore,
          developerInviteBank: this.developerInviteStore,
          policyBank: this.policyStore,
          intercomBank: this.intercomBank,
          planBank: this.plansBank
        };
      },
      clients: this.clientProvisioner
    };

    // Stores
    this.mfaStore = new MFAStore(this, opts);
    tokenProvisioner.sensitiveUserHubToken.setProvisioningProvider(this.mfaStore.requestMFAAuthenticationToken);

    this.tokenStore = new TokenStore();
    this.intercomBank = new IntercomTicketBank(opts);
    this.orgStore = new OrgStore(opts);
    this.notificationStore = new NotificationStore(this);
    this.profileStore = new ProfileStore(this);

    this.appsStore = new AppsBank(opts);
    this.plansBank = new PlansBank(opts);
    this.developStore = new DeveloperBank(opts);
    this.developerInviteStore = new DeveloperInviteStore(opts);
    this.roleStore = new RoleBank(opts);
    this.integrationStore = new IntegrationBank(opts);
    this.deployConfigStore = new DeploymentBank(opts);
    this.policyStore = new PolicyBank(opts);

    // Loggers
    this.flightLogger = new FlightLogger(this, { tokenStore: this.tokenStore });

    // clients
    this.sessionAccountsPortalClient = this.clientProvisioner.provisionSessionAccountsPortalClient();
    this.accountV5Client = this.clientProvisioner.provisionAccountsV5Client() as any;
    this.cardinalClient = this.clientProvisioner.provisionCardinalClient();

    // Layers
    this.layerManager = new LayerManager();
    this.dialogStore = new DialogStore(this);

    makeObservable(this);
  }

  getRegions(trial: boolean): Region[] {
    if (trial) {
      return [TRIAL_REGIONS[this.context.environment]];
    }
    return getRegionsForEnvironment(this.context.environment);
  }

  async loadUserPermissions(): Promise<Permission[]> {
    if (!this.userPermissionsPromise && this.context.USER_ID) {
      this.userPermissionsPromise = this.cardinalClient
        .getPermissionsForUser({ user_id: this.context.USER_ID })
        .then((perms: Permission[]) => {
          return perms || [];
        });
    }
    return this.userPermissionsPromise!;
  }

  async reloadUserPermissions() {
    this.userPermissionsPromise = undefined;
    return this.loadUserPermissions();
  }
}

// null as any is fine in this instance
export const SystemContext = React.createContext<System>(null as any);
